import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import {
  AGGREGATION_FIELDS,
  ASSET_TYPES,
  buildQueryFilterFromClauses,
  createAggregationParams,
  createServicePrefixClause,
  createShouldClause,
  createTermClause,
  type ElasticsearchQueryFilter,
  FILTER_GROUP_TYPES,
  getAssetTypeName,
  INDICES,
  QUERY_FIELD_NAMES,
  SERVICE_ASSET_TYPE_MAP,
} from '@/shared/api/openmetadata';
import type { OpenMetadataAggregations, OpenMetadataResponse } from '@/shared/api/openmetadata';

import { isExcludedFromDiscoveryAssetTypeFilter } from '@/entities/asset-search/domain/constants';
import type { FilterGroupType, FilterOption, FilterState } from '@/entities/discovery/domain/types';

/**
 * 필터 상태를 Elasticsearch 쿼리 필터로 변환
 *
 * @description
 * 사용자가 선택한 필터(자산 타입, 소유자, 태그, 티어, 성숙도, 데이터 소스)를
 * Elasticsearch의 bool 쿼리로 변환합니다.
 *
 * 특수 처리:
 * - Certification(성숙도): OpenMetadata의 certification 태그 대신 서비스명 prefix로 필터링
 *   - Bronze → "bronze-*" 서비스명
 *   - Silver → "silver-*" 서비스명
 *   - Gold → "gold-*" 서비스명 AND (feature_set OR dataset)
 * - Data Source: serviceType과 service.displayName을 동적으로 구분하여 쿼리 생성
 *
 * @param filters - 현재 필터 상태
 * @param excludeGroup - 제외할 필터 그룹 (해당 필터의 옵션을 조회할 때 자기 자신을 제외)
 * @returns Elasticsearch 쿼리 필터 또는 undefined (필터가 없는 경우)
 */
const buildQueryFilter = (
  filters: FilterState,
  excludeGroup?: FilterGroupType
): ElasticsearchQueryFilter | undefined => {
  const mustClauses: Array<Record<string, unknown>> = [];

  if (excludeGroup !== FILTER_GROUP_TYPES.ASSET_TYPE && filters.assetType && filters.assetType.length > 0) {
    mustClauses.push(createShouldClause(QUERY_FIELD_NAMES.ENTITY_TYPE, filters.assetType));
  }

  if (excludeGroup !== FILTER_GROUP_TYPES.OWNER && filters.owner && filters.owner.length > 0) {
    mustClauses.push(createShouldClause(QUERY_FIELD_NAMES.OWNERS_DISPLAY_NAME, filters.owner));
  }

  if (excludeGroup !== FILTER_GROUP_TYPES.TAGS && filters.tags && filters.tags.length > 0) {
    mustClauses.push(createShouldClause(QUERY_FIELD_NAMES.TAGS_FQN, filters.tags));
  }

  if (excludeGroup !== FILTER_GROUP_TYPES.TIER && filters.tier && filters.tier.length > 0) {
    mustClauses.push(createShouldClause(QUERY_FIELD_NAMES.TIER_FQN, filters.tier));
  }

  if (excludeGroup !== FILTER_GROUP_TYPES.CERTIFICATION && filters.certification && filters.certification.length > 0) {
    const certificationClauses: Array<Record<string, unknown>> = [];

    filters.certification.forEach((certFqn) => {
      const certLevel = certFqn.toLowerCase().split('.').pop();

      if (certLevel === 'bronze' || certLevel === 'silver') {
        certificationClauses.push(createServicePrefixClause(`${certLevel}-`));
      } else if (certLevel === 'gold') {
        certificationClauses.push({
          bool: {
            must: [
              createServicePrefixClause('gold-'),
              {
                bool: {
                  should: [
                    createTermClause(QUERY_FIELD_NAMES.ENTITY_TYPE, ASSET_TYPES.FEATURE_SET),
                    createTermClause(QUERY_FIELD_NAMES.ENTITY_TYPE, ASSET_TYPES.DATASET),
                  ],
                  minimum_should_match: 1,
                },
              },
            ],
          },
        });
      }
    });

    if (certificationClauses.length > 0) {
      mustClauses.push({
        bool: {
          should: certificationClauses,
          minimum_should_match: 1,
        },
      });
    }
  }

  if (excludeGroup !== FILTER_GROUP_TYPES.DATA_SOURCE && filters.dataSource && filters.dataSource.length > 0) {
    const dataSourceShould: Array<{ term: Record<string, string> }> = [];
    const knownServiceTypes = Object.keys(SERVICE_ASSET_TYPE_MAP);

    filters.dataSource.forEach((source) => {
      const lowerSource = source.toLowerCase();
      if (knownServiceTypes.includes(lowerSource)) {
        dataSourceShould.push({ term: { [QUERY_FIELD_NAMES.SERVICE_TYPE]: lowerSource } });
      } else {
        dataSourceShould.push({ term: { [QUERY_FIELD_NAMES.SERVICE_DISPLAY_NAME]: source } });
      }
    });

    if (dataSourceShould.length > 0) {
      mustClauses.push({ bool: { should: dataSourceShould } });
    }
  }

  if (mustClauses.length === 0) {
    return undefined;
  }

  return buildQueryFilterFromClauses(mustClauses);
};

export const getFilteredOptions = async (
  targetGroup: FilterGroupType,
  currentFilters: FilterState,
  excludeGroup?: FilterGroupType
): Promise<FilterOption[]> => {
  try {
    const queryFilter = buildQueryFilter(currentFilters, excludeGroup);
    const params = createAggregationParams(INDICES.DATA_ASSET, queryFilter);

    const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
      params,
      paramsSerializer: {
        encode: (value) => encodeURIComponent(value),
      },
    });

    return mapAggregationsToFilterOptions(response.data.aggregations, targetGroup, currentFilters);
  } catch (error) {
    console.error('Failed to fetch filtered options:', error);
    throw error;
  }
};

const AGGREGATION_MAPPERS: Record<
  FilterGroupType,
  (aggregations: OpenMetadataAggregations, currentFilters?: FilterState) => FilterOption[]
> = {
  [FILTER_GROUP_TYPES.DATA_SOURCE]: (aggregations) => mapDataSourceOptions(aggregations),
  [FILTER_GROUP_TYPES.ASSET_TYPE]: (aggregations, currentFilters) => mapAssetTypeOptions(aggregations, currentFilters),
  [FILTER_GROUP_TYPES.OWNER]: (aggregations) => mapOwnerOptions(aggregations),
  [FILTER_GROUP_TYPES.TIER]: (aggregations) => mapTierOptions(aggregations),
  [FILTER_GROUP_TYPES.TAGS]: (aggregations) => mapTagOptions(aggregations),
  [FILTER_GROUP_TYPES.CERTIFICATION]: (aggregations) => mapCertificationOptions(aggregations),
  [FILTER_GROUP_TYPES.DOMAIN]: () => [],
} as const;

const mapAggregationsToFilterOptions = (
  aggregations: OpenMetadataAggregations | undefined,
  targetGroup: FilterGroupType,
  currentFilters?: FilterState
): FilterOption[] => {
  if (!aggregations) return [];

  return AGGREGATION_MAPPERS[targetGroup]?.(aggregations, currentFilters) || [];
};

const mapDataSourceOptions = (aggregations: OpenMetadataAggregations): FilterOption[] => {
  const serviceTypeBuckets = aggregations[AGGREGATION_FIELDS.SERVICE_TYPE]?.buckets || [];
  const serviceNameBuckets = aggregations[AGGREGATION_FIELDS.SERVICE_NAME]?.buckets || [];

  const options: FilterOption[] = [];

  serviceTypeBuckets.forEach((bucket) => {
    options.push({
      id: bucket.key,
      title: bucket.key.toUpperCase(),
      label: bucket.key.toUpperCase(),
      value: bucket.key,
      count: bucket.doc_count,
    });
  });

  serviceNameBuckets.forEach((bucket) => {
    if (!options.some((opt) => opt.value === bucket.key)) {
      options.push({
        id: bucket.key,
        title: bucket.key,
        label: bucket.key,
        value: bucket.key,
        count: bucket.doc_count,
      });
    }
  });

  return options.sort((a, b) => (b.count || 0) - (a.count || 0));
};

const mapAssetTypeOptions = (aggregations: OpenMetadataAggregations, currentFilters?: FilterState): FilterOption[] => {
  const buckets = aggregations[AGGREGATION_FIELDS.ENTITY_TYPE]?.buckets || [];

  let filteredBuckets = buckets;

  if (currentFilters?.dataSource && currentFilters.dataSource.length > 0) {
    const compatibleAssetTypes = getCompatibleAssetTypes(currentFilters.dataSource);
    if (compatibleAssetTypes.length > 0) {
      filteredBuckets = buckets.filter((bucket) => compatibleAssetTypes.includes(bucket.key));
    }
  }

  filteredBuckets = filteredBuckets.filter((bucket) => !isExcludedFromDiscoveryAssetTypeFilter(bucket.key));

  return filteredBuckets
    .map((bucket) => ({
      id: bucket.key,
      title: getAssetTypeName(bucket.key),
      label: getAssetTypeName(bucket.key),
      value: bucket.key,
      count: bucket.doc_count,
    }))
    .sort((a, b) => (b.count || 0) - (a.count || 0));
};

const mapOwnerOptions = (aggregations: OpenMetadataAggregations): FilterOption[] => {
  const buckets = aggregations[AGGREGATION_FIELDS.OWNER]?.buckets || [];

  return buckets
    .map((bucket) => ({
      id: bucket.key,
      title: bucket.key,
      label: bucket.key,
      value: bucket.key,
      count: bucket.doc_count,
    }))
    .sort((a, b) => (b.count || 0) - (a.count || 0));
};

const mapTierOptions = (aggregations: OpenMetadataAggregations): FilterOption[] => {
  const buckets = aggregations[AGGREGATION_FIELDS.TIER]?.buckets || [];

  return buckets
    .map((bucket) => ({
      id: bucket.key,
      title: bucket.key,
      label: bucket.key,
      value: bucket.key,
      count: bucket.doc_count,
    }))
    .sort((a, b) => (b.count || 0) - (a.count || 0));
};

const mapTagOptions = (aggregations: OpenMetadataAggregations): FilterOption[] => {
  const buckets = aggregations[AGGREGATION_FIELDS.TAGS]?.buckets || [];

  return buckets
    .map((bucket) => ({
      id: bucket.key,
      title: bucket.key.split('.').pop() || bucket.key,
      label: bucket.key.split('.').pop() || bucket.key,
      value: bucket.key,
      count: bucket.doc_count,
    }))
    .sort((a, b) => (b.count || 0) - (a.count || 0));
};

const mapCertificationOptions = (aggregations: OpenMetadataAggregations): FilterOption[] => {
  const buckets = aggregations[AGGREGATION_FIELDS.CERTIFICATION]?.buckets || [];

  return buckets
    .map((bucket) => ({
      id: bucket.key,
      title: bucket.key.split('.').pop() || bucket.key,
      label: bucket.key.split('.').pop() || bucket.key,
      value: bucket.key,
      count: bucket.doc_count,
    }))
    .sort((a, b) => (b.count || 0) - (a.count || 0));
};

export const getCompatibleAssetTypes = (dataSourceTypes: string[]): string[] => {
  if (dataSourceTypes.length === 0) return [];

  const compatibleTypes = new Set<string>();

  dataSourceTypes.forEach((dataSource) => {
    const lowerDataSource = dataSource.toLowerCase();
    const assetTypes = SERVICE_ASSET_TYPE_MAP[lowerDataSource] || [];
    assetTypes.forEach((type) => compatibleTypes.add(type));
  });

  return Array.from(compatibleTypes);
};
